import { FC, useMemo, useState } from "react"
import { RateFlowDetails } from "../../../types/rateflow"
import { Brand } from "../../../types"
import {
  PricingEngineType,
  PRICING_ENGINE_LABELS
} from "../../../types/pricing-engine"
import { useFetchAllBrandsQuery } from "../../../redux/queries"
import { Search, Check, Building2, Zap } from "lucide-react"

// Pricing engine logos
import obLogo from "../../../assets/images/ob-logo.png"
import pollyLogo from "../../../assets/images/polly-logo.png"
import loanSifterLogo from "../../../assets/images/loan-sifter-logo.webp"
import lenderPriceLogo from "../../../assets/images/lenderprice-logo.jpg"
import mortechLogo from "../../../assets/images/mortech-logo.png"

const ENGINE_LOGOS: Partial<Record<PricingEngineType, string>> = {
  optimal_blue: obLogo,
  polly: pollyLogo,
  loan_sifter: loanSifterLogo,
  lender_price: lenderPriceLogo,
  mortech: mortechLogo
}

const ENGINE_FILTERS: { id: PricingEngineType | "all"; label: string }[] = [
  { id: "all", label: "All Engines" },
  { id: "optimal_blue", label: "Optimal Blue" },
  { id: "polly", label: "Polly" },
  { id: "loan_sifter", label: "Loan Sifter" },
  { id: "lender_price", label: "LenderPrice" },
  { id: "mortech", label: "Mortech" },
  { id: "loanpass", label: "LoanPass" },
  { id: "mortgage_bot", label: "MortgageBot" }
]

interface Props {
  rateflows: RateFlowDetails[]
  isLoading: boolean
  selectedRateflowId: string
  onSelectRateflow: (id: string) => void
}

const AdminRateflowBrowser: FC<Props> = ({
  rateflows,
  isLoading,
  selectedRateflowId,
  onSelectRateflow
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [engineFilter, setEngineFilter] = useState<PricingEngineType | "all">(
    "all"
  )

  // Fetch all brands to get their names
  const { data: brands } = useFetchAllBrandsQuery({})

  // Build a map from brand ID to brand name
  const brandNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    if (brands) {
      brands.forEach((brand: Brand) => {
        if (brand.id) map[brand.id] = brand.name || brand.id
        if (brand.nid) map[brand.nid] = brand.name || brand.nid
      })
    }
    return map
  }, [brands])

  const getBrandName = (bankId: string) => {
    return brandNameMap[bankId] || bankId
  }

  // Group rateflows by engine type for counts
  const engineCounts = useMemo(() => {
    const counts: Record<string, number> = { all: rateflows.length }
    rateflows.forEach(rf => {
      const engine = rf.settings?.api_mode
      if (engine) {
        counts[engine] = (counts[engine] || 0) + 1
      }
    })
    return counts
  }, [rateflows])

  // Filter rateflows based on search and engine filter
  const filteredRateflows = useMemo(() => {
    return rateflows.filter(rf => {
      // Engine filter
      if (engineFilter !== "all") {
        if (rf.settings?.api_mode !== engineFilter) return false
      }

      // Search filter (by name, brand name, or bank_id)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase()
        const nameMatch = rf.name?.toLowerCase().includes(query)
        const brandName = getBrandName(rf.bank_id)
        const brandMatch = brandName?.toLowerCase().includes(query)
        const bankIdMatch = rf.bank_id?.toLowerCase().includes(query)
        if (!nameMatch && !brandMatch && !bankIdMatch) return false
      }

      return true
    })
  }, [rateflows, engineFilter, searchQuery, brandNameMap])

  const getEngineLogo = (engine?: PricingEngineType) => {
    if (!engine) return null
    return ENGINE_LOGOS[engine]
  }

  const getEngineLabel = (engine?: PricingEngineType) => {
    if (!engine) return "Unknown"
    return PRICING_ENGINE_LABELS[engine] || engine
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl shadow-lg shadow-violet-500/20">
          <Zap size={22} className="text-white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-neutral-900">
            Pricing Engine Configurations
          </h2>
          <p className="text-sm text-neutral-500">
            Browse and manage all rateflow configurations across the platform
          </p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-gradient-to-br from-neutral-50 to-neutral-100/50 rounded-2xl p-5 border border-neutral-200/60">
        {/* Search Input */}
        <div className="relative mb-5">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by rateflow name or brand..."
            className="w-full pl-11 pr-4 py-3 bg-white border border-neutral-200 rounded-xl text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-all"
          />
        </div>

        {/* Engine Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {ENGINE_FILTERS.map(filter => {
            const count = engineCounts[filter.id] || 0
            const isActive = engineFilter === filter.id
            const logo = filter.id !== "all" ? getEngineLogo(filter.id) : null

            // Skip engines with no rateflows (except "all")
            if (filter.id !== "all" && count === 0) return null

            return (
              <button
                key={filter.id}
                onClick={() => setEngineFilter(filter.id)}
                className={`
                  group flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                  ${
                    isActive
                      ? "bg-white text-neutral-900 shadow-md shadow-neutral-200/50 ring-1 ring-neutral-200"
                      : "bg-transparent text-neutral-600 hover:bg-white/60 hover:text-neutral-900"
                  }
                `}
              >
                {logo && (
                  <img
                    src={logo}
                    alt={filter.label}
                    className="h-4 w-auto object-contain opacity-80 group-hover:opacity-100"
                  />
                )}
                <span>{filter.label}</span>
                <span
                  className={`
                  ml-1 px-1.5 py-0.5 rounded text-xs
                  ${
                    isActive
                      ? "bg-violet-100 text-violet-700"
                      : "bg-neutral-200/60 text-neutral-500"
                  }
                `}
                >
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-8 text-center text-neutral-500">
            <div className="inline-block w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mb-3" />
            <p>Loading configurations...</p>
          </div>
        ) : filteredRateflows.length === 0 ? (
          <div className="p-8 text-center text-neutral-500">
            <Building2 size={32} className="mx-auto mb-3 opacity-40" />
            <p>No rateflows match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-100 bg-neutral-50/50">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    Brand
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    Rateflow
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    Engine
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {filteredRateflows.map(rf => {
                  const isSelected = rf.id === selectedRateflowId
                  const engine = rf.settings?.api_mode
                  const logo = getEngineLogo(engine)
                  const isDefault = rf.default === "1"

                  return (
                    <tr
                      key={rf.id}
                      onClick={() => onSelectRateflow(rf.id)}
                      className={`
                        cursor-pointer transition-all
                        ${
                          isSelected
                            ? "bg-violet-50/80 hover:bg-violet-50"
                            : "hover:bg-neutral-50"
                        }
                      `}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`
                            w-9 h-9 rounded-lg flex items-center justify-center text-sm font-semibold
                            ${
                              isSelected
                                ? "bg-violet-100 text-violet-700"
                                : "bg-neutral-100 text-neutral-600"
                            }
                          `}
                          >
                            {getBrandName(rf.bank_id)
                              ?.charAt(0)
                              .toUpperCase() || "?"}
                          </div>
                          <span className="font-medium text-neutral-900">
                            {getBrandName(rf.bank_id)}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-neutral-700">{rf.name}</span>
                          {isSelected && (
                            <Check
                              size={16}
                              className="text-violet-600 shrink-0"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          {logo && (
                            <img
                              src={logo}
                              alt={getEngineLabel(engine)}
                              className="h-4 w-auto object-contain"
                            />
                          )}
                          <span className="text-sm text-neutral-600">
                            {getEngineLabel(engine)}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        {isDefault ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            Default
                          </span>
                        ) : (
                          <span className="text-xs text-neutral-400">-</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Results Footer */}
        {!isLoading && filteredRateflows.length > 0 && (
          <div className="px-5 py-3 bg-neutral-50/50 border-t border-neutral-100 text-sm text-neutral-500">
            Showing {filteredRateflows.length} of {rateflows.length} rateflows
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminRateflowBrowser
